import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { uniq } from 'lodash-es';
import { concat, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  mapTo,
  mergeMap,
  switchMap,
  take,
  toArray,
  withLatestFrom,
} from 'rxjs/operators';

import { MarketComparisonActions } from './market-comparison.actions';
import { MarketComparisonSelectors } from './market-comparison.selectors';
import { resolveBuiltInChecklistTopicIds } from '../../features/checklist/utils/built-in-checklist-topics';
import { ViewerCommand } from '../../features/document-viewer/models/document-viewer.models';
import { DEFAULT_ROW_HEIGHT } from '../../features/market-comparison/constants/default-column';
import { dateAdjustmentToText } from '../../features/market-comparison/models/date-adjustment.util';
import { DiffWorkerOutput, SimpleDiffItem } from '../../features/market-comparison/models/paragraph-diff.model';
import { Topic } from '../../features/topic/models/topic.model';
import { DefinedTermMetaService } from '../../features/market-comparison/services/defined-term-meta.service';
import { DefinedTermService } from '../../features/market-comparison/services/defined-term.service';
import { DiffItemService } from '../../features/market-comparison/services/diff-item.service';
import { MarketComparisonService } from '../../features/market-comparison/services/market-comparison.service';
import { FAIL_NOTIFICATION_CONFIGURATION } from '../../features/shared/constants/notification-configurations';
import { stringify } from '../../features/shared/constants/string-helpers';
import {
  QualityTrafficLightMapper,
  QualityTrafficLights,
} from '../../features/shared/models/common/quality-traffic-lights.enum';
import { DiffService } from '../../features/shared/services/diff.service';
import { arrayToRecord } from '../../features/shared/utils/array-to-record';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { DatapointFieldControlType } from '../../features/unified-datapoint/models/datapoint-field.models';
import { DatapointChangedEvent } from '../../features/unified-datapoint/models/datapoint.model';
import { Datapoint } from '../../generated/graphql-models';
import { AppActions } from '../app/app.actions';
import { AppSelectors } from '../app/app.selectors';
import { DealActions } from '../deal/deal.actions';
import { DealComparisonActions } from '../deal-comparison/deal-comparison.actions';
import { DealFilterActions } from '../deal-filter/deal-filter.actions';
import { DealFilterModePage } from '../deal-filter/deal-filter.state';
import { ExtractActions } from '../extract/extract.actions';
import { RootState } from '../root-state.model';
import { TopicActions } from '../topic/topic.actions';
import { TopicListActions } from '../topic-list/topic-list.actions';
import { TransactionSelectors } from '../transaction/transaction.selectors';

/** Diff text for a datapoint: date values compare the dates they render, not their adjustment wrapper JSON. */
function diffTextOfDatapoint(datapoint: Partial<Datapoint> | undefined): string {
  return dateAdjustmentToText(datapoint?.value) ?? (datapoint?.value as string);
}

@Injectable()
export class MarketComparisonEffects {
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly definedTermMetaService = inject(DefinedTermMetaService);
  private readonly marketComparisonService = inject(MarketComparisonService);
  private readonly definedTermService = inject(DefinedTermService);
  private readonly diffItemService = inject(DiffItemService);
  private readonly diffService = inject(DiffService);

  LoadTerms$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.LoadTermList),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        return this.definedTermService.getDefinedTermSuggestions(action.partialTerm).pipe(
          map((list) =>
            MarketComparisonActions.LoadTermListSuccess({
              partialTerm: action.partialTerm,
              list: uniq(list.map((d) => d.definedTerm)),
              listStatistics: list.reduce((acc, definedTerm) => {
                acc[definedTerm.definedTerm] = `${definedTerm.documentCount} documents`;
                return acc;
              }, {}),
            })
          ),
          reportEffectError(MarketComparisonActions.LoadTermListFail)
        );
      })
    )
  );

  LoadTermMeta$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.LoadTermMeta),
      withLatestFrom(this.store),
      mergeMap(([action, store]) =>
        (store.marketComparison.termMeta[action.termId]
          ? of({ meta: store.marketComparison.termMeta[action.termId], statistics: {} })
          : this.definedTermMetaService.getAllByTerm(action.termId)
        ).pipe(
          map((meta) => MarketComparisonActions.LoadTermMetaSuccess({ meta })),
          reportEffectError(MarketComparisonActions.LoadTermMetaFail)
        )
      )
    )
  );

  loadStatistics$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.LoadStatistics),
      withLatestFrom(this.store),
      map(([action, state]) => action.terms.filter((t) => !state.marketComparison.termAdditionalInfo[t])),
      filter((terms) => !!terms.length),
      switchMap((terms) =>
        this.definedTermService.getByTerms(terms).pipe(
          map((statistics) => MarketComparisonActions.LoadStatisticsSuccess({ statistics })),
          reportEffectError(MarketComparisonActions.LoadStatisticsFail)
        )
      )
    )
  );

  setPreview$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.SetPreview),
      filter((action) => !action.preview),
      withLatestFrom(this.store),
      map(([action, store]) => TopicActions.LoadTopic({ topicId: store.topic.topicId }))
    )
  );

  makeDiff$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.MakeDiff),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        store.marketComparison.diffColumns[action.columnId] && !action.baseline
          ? of(MarketComparisonActions.SetDiff({ columnId: action.columnId, value: false }))
          : this.store
              .select(MarketComparisonSelectors.orderedAndSelectedTransactionIds(DealFilterModePage.MarketCompare))
              .pipe(
                switchMap((ids) => this.store.select(MarketComparisonSelectors.marketCompareMatrix(ids))),
                take(1),
                switchMap((matrix) => {
                  const columnIndex = matrix.columnHeaders.findIndex(
                    (c) => c.id === action.columnId && this.diffItemService.isDiffable(c.value)
                  );

                  if (columnIndex === -1) {
                    console.error('Column is not diffable');
                    return of(MarketComparisonActions.MakeDiffFail({ error: 'Column is not diffable' }));
                  }

                  try {
                    const transactionIds = matrix.rows.map((row) => row?.transaction?.transactionId);
                    const cellValues = matrix.rows.map((row) => row.values[columnIndex]);
                    const diff$: Observable<DiffWorkerOutput> = this.diffItemService.diffWithWorker(
                      action.baseline,
                      cellValues,
                      transactionIds
                    );

                    return diff$.pipe(
                      map((result) => {
                        return MarketComparisonActions.MakeDiffSuccess({
                          columnId: action.columnId,
                          baseline: result.baseline,
                          diffValues: result.diffValues,
                          diffMaximums: result.diffMaximums,
                        });
                      }),
                      reportEffectError(MarketComparisonActions.MakeDiffFail)
                    );
                  } catch (error) {
                    console.error(error);
                    return of(MarketComparisonActions.MakeDiffFail({ error: stringify(error) }));
                  }
                })
              )
      )
    )
  );

  makeDiffSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.MakeDiffSuccess),
      withLatestFrom(this.store),
      map(([action, store]) => MarketComparisonActions.SetDiff({ columnId: action.columnId, value: true }))
    )
  );

  templateModalClosedEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.TemplateModalClosed),
      filter((action) => action.modalId === 'edit-topic' && !!action.data),
      map((action) => {
        if (!action.data.id) {
          const columns = (action.additionalInfo as { columns?: unknown[] })?.columns ?? [];
          return TopicListActions.CreateTopic({
            topic: {
              ...action.data,
              name: action.data.displayName,
              id: undefined,
              rowHeight: DEFAULT_ROW_HEIGHT,
              columns,
            } as Topic,
          });
        }
        return TopicActions.SaveTopicDetails({
          data: action.data,
        });
      }),
    ),
  );

  openSummaryPicker$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.OpenSummaryPicker),
      withLatestFrom(
        this.store,
        this.store.select(MarketComparisonSelectors.orderedAndSelectedTransactionIds(DealFilterModePage.MarketCompare)),
        this.store.select(TransactionSelectors.listLookup),
        this.store.select(AppSelectors.builtInChecklistTopicIdsByAsset)
      ),
      switchMap(([action, store, orderedTransactionIds, tranactions, builtInChecklistTopicIdsByAsset]) => {
        let transactionId = action.transactionId;

        if (!transactionId) {
          orderedTransactionIds.forEach((id) => {
            if (!transactionId) {
              transactionId = id;
            }

            if (
              QualityTrafficLightMapper[tranactions[id].qualityTrafficLight] >
              QualityTrafficLightMapper[tranactions[transactionId].qualityTrafficLight]
            ) {
              transactionId = id;
            }

            if (tranactions[transactionId].qualityTrafficLight === QualityTrafficLights.Green) {
            }
          });
        }

        const transaction = tranactions[transactionId];

        // The picker renders the deal's built-in topics (mirrors summaryPickerTopics$ in the container).
        // Their columns are not part of the selected checklist, so we must explicitly load their values —
        // otherwise the cells render with titles but no data.
        const dealAssets = transaction.assetShortNames?.length
          ? transaction.assetShortNames
          : [transaction.assetShortName].filter(Boolean);
        const checklistTopicIds = resolveBuiltInChecklistTopicIds(dealAssets, builtInChecklistTopicIdsByAsset);
        const topicById = arrayToRecord(store.topicList.topics ?? [], 'id');
        const dealAssetSet = new Set(dealAssets);
        const pickerTopicIds = checklistTopicIds
          ? checklistTopicIds.filter((id) => !!topicById[id])
          : (store.topicList.topics ?? [])
              .filter((topic) => !!topic.builtIn && (topic.assetShortName ?? []).some((asset) => dealAssetSet.has(asset)))
              .map((topic) => topic.id);

        return of(
          DealActions.LoadDeal({ transaction, skipActivityLog: true }),
          ExtractActions.StartLoadList({ topicIds: pickerTopicIds, transactionIds: [transactionId] })
        );
      })
    )
  );

  clearDiffSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MarketComparisonActions.UseTopic,
        DealFilterActions.UseFilter,
        DealComparisonActions.AddToBasket,
        DealComparisonActions.RemoveFromBasket,
        DealComparisonActions.SetBasket
      ),
      map(() => MarketComparisonActions.ClearDiff())
    )
  );

  updateDatapointEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.UpdateDatapoint),
      withLatestFrom(this.store),
      concatMap(([action, store]) => {
        return this.marketComparisonService
          .upsertDatapoint(
            action.event.columnId,
            action.event.transactionId,
            action.event.datapointHash,
            action.event.value,
            action.event.sourceReferences
          )
          .pipe(
            map((datapoint) => {
              return MarketComparisonActions.UpdateDatapointSuccess({
                datapoint,
                event: action.event,
              });
            }),

            reportEffectError(MarketComparisonActions.UpdateDatapointFail)
          );
      })
    )
  );

  updateDatapointsEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.UpdateDatapoints),
      withLatestFrom(this.store),
      concatMap(([action, store]) => {
        return this.marketComparisonService.upsertDatapoints(action.event.columnId, action.event.datapoints).pipe(
          map(({ datapoints, failedLibraryTransactionIds }) => {
            return MarketComparisonActions.UpdateDatapointsSuccess({
              datapoints,
              failedLibraryTransactionIds,
              columnId: action.event.columnId,
            });
          }),

          reportEffectError(MarketComparisonActions.UpdateDatapointsFail)
        );
      })
    )
  );

  clearAllGroundTruthEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.ClearAllGroundTruth),
      withLatestFrom(this.store),
      concatMap(([action, store]) => {
        return this.marketComparisonService.clearAllGroundTruth(action.event).pipe(
          map(({ isSucceeded }) =>
            MarketComparisonActions.ClearAllGroundTruthSuccess({ event: action.event, isSucceeded })
          ),
          reportEffectError(MarketComparisonActions.ClearAllGroundTruthFail)
        );
      })
    )
  );

  clearAllGroundTruthSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.ClearAllGroundTruthSuccess),
      map((action) =>
        AppActions.PushNotification({
          message: action.isSucceeded ? 'Datapoints are updated' : `Failed to update datapoints`,
          config: action.isSucceeded ? 'success' : 'fail',
        })
      )
    )
  );

  viewerModifyDatapoint$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.ViewerModifyDatapoint),
      filter((action) => {
        const [viewerType, viewerValue] = action.viewerPayload;
        return (
          viewerType === ViewerCommand.SelectedSourceReference ||
          [
            DatapointFieldControlType.Textbox,
            DatapointFieldControlType.MultilineTextbox,
            DatapointFieldControlType.Number,
          ].includes(action.target.datapointFieldControlType)
        );
      }),
      mergeMap((action) => {
        return this.store.select(MarketComparisonSelectors.updatingDatapoint).pipe(
          filter((val) => !val),
          take(1),
          mapTo(action)
        );
      }),
      withLatestFrom(this.store),
      map(([action, store]) => {
        const datapoint = (
          store.topic.list[action.target.columnId]
            ? store.topic.list[action.target.columnId][action.target.transactionId]
            : undefined
        ) as Partial<Datapoint>;

        return {
          action,
          transactionId: action.target.transactionId,
          datapointHash: action.target.datapointHash,
          columnId: action.target.columnId,
          datapoint,
        };
      }),
      map(({ action, transactionId, columnId, datapointHash, datapoint }) => {
        const [viewerType, viewerValue] = action.viewerPayload;
        let value = action.value || datapoint?.value;
        let sourceReferences = datapoint?.sourceReferences || [];

        switch (viewerType) {
          case ViewerCommand.AppendData:
            value = value ? `${value} ${viewerValue}` : viewerValue;
            break;
          case ViewerCommand.ReplaceData:
            value = viewerValue;
            break;
          case ViewerCommand.SelectedSourceReference:
            sourceReferences = [...sourceReferences, viewerValue];
            break;

          default:
            break;
        }

        const event = {
          transactionId,
          columnId,
          datapointHash,
          value,
          sourceReferences,
        };

        return MarketComparisonActions.UpdateDatapoint({
          event,
        });
      })
    )
  );

  updateDatapointsSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.UpdateDatapointsSuccess),
      map((action) =>
        AppActions.PushNotification({
          message: action.failedLibraryTransactionIds.length
            ? `Failed to update datapoints: ` + action.failedLibraryTransactionIds.join(', ')
            : 'Datapoints are updated',
          config: action.failedLibraryTransactionIds.length ? 'fail' : 'success',
        })
      )
    )
  );

  updateDatapointFail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.UpdateDatapointFail),
      map(() =>
        AppActions.PushNotification({
          message: 'Failed to update the datapoint',
          config: FAIL_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  setAsGroundTruth$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.SetAsGroundTruth),
      withLatestFrom(this.store),
      map(([action, store]) => {
        const datapoint = store.extractionRuleList.rules?.find((r) => r.id === action.event.datapointId);
        const datapointHash = store.datapointFieldList.fields?.find(
          (f) => f.id === datapoint.groundTruthDatapointField
        )?.fieldHash;
        const columnId = store.topic.columns.find((c) => c.value === datapoint.groundTruthDatapointField)?.id;

        return MarketComparisonActions.UpdateDatapoint({
          event: {
            transactionId: action.event.transactionId,
            columnId,
            datapointHash,
            value: action.event.value,
            sourceReferences: action.event.sourceReferences,
          } as DatapointChangedEvent,
        });
      })
    )
  );

  makeGroundTruthDiff$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketComparisonActions.MakeGroundTruthDiff),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        const columnValues = store.topic.list[action.columnId];
        const groundTruthValues = store.topic.list[action.groundTruthColumnId];

        if (!action.enabled) {
          return of(
            MarketComparisonActions.MakeGroundTruthDiffSuccess({
              columnId: action.columnId,
              diffValues: undefined,
              diffMaximums: undefined,
            })
          );
        }

        const diffs$ = Object.keys(columnValues).map((transactionId) => {
          const value = diffTextOfDatapoint(columnValues[transactionId] as Partial<Datapoint>);
          const groundTruthValue = diffTextOfDatapoint(groundTruthValues[transactionId] as Partial<Datapoint>);

          return this.diffService.calculateSimpleDiff(value, groundTruthValue).pipe(
            map((diff) => {
              return {
                transactionId: transactionId,
                text: value,
                diff,
              };
            })
          );
        });

        return concat(...diffs$).pipe(
          toArray(),
          map((diffs) => {
            return MarketComparisonActions.MakeGroundTruthDiffSuccess({
              columnId: action.columnId,
              diffValues: diffs.reduce((acc, diff) => {
                acc[diff.transactionId] = [
                  {
                    text: diff.text,
                    diffHtml: diff.diff.diffHtml,
                    score: diff.diff.score,
                  } as SimpleDiffItem,
                ];
                return acc;
              }, {}),
              diffMaximums: diffs.reduce((acc, diff) => {
                acc[diff.transactionId] = diff.diff.score;
                return acc;
              }, {}),
            });
          }),
          catchError((error) => of(MarketComparisonActions.MakeGroundTruthDiffFail({ error })))
        );
      })
    )
  );
}
